"use client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, MessageSquare } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/app/admin/_common/ui/sheet";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoButton from "@/app/admin/_common/CuoButton";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import { formatUtcDateTime } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import CommunicationsService, {
  Message,
  MessageDetail,
} from "@/app/_services/CommunicationsService";

import {
  SOURCE_LABELS,
  statusBadgeClasses,
  statusDotClasses,
  statusLabel,
} from "./messageStatus";

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="grid gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="break-words text-[13px] text-foreground">{value || "-"}</span>
    </div>
  );
}

const EVENT_LABELS: Record<string, string> = {
  queued: "Queued",
  sent: "Sent",
  delivered: "Delivered",
  opened: "Opened",
  clicked: "Clicked",
  bounced: "Bounced",
  complained: "Marked as spam",
  undelivered: "Undelivered",
  failed: "Failed",
  canceled: "Canceled",
};

function Timeline({ events }: { events: MessageDetail["events"] }) {
  if (!events?.length) {
    return (
      <span className="text-[12.5px] text-muted-foreground">
        Nothing reported back yet.
      </span>
    );
  }
  return (
    <ol className="flex flex-col gap-2.5">
      {events.map((event, i) => (
        <li key={i} className="flex gap-2.5">
          <span
            className={cn(
              "mt-1.5 size-1.5 flex-none rounded-full",
              statusDotClasses(event.event_type),
            )}
          />
          <div className="flex min-w-0 flex-col">
            <span className="text-[12.5px] text-foreground">
              {EVENT_LABELS[event.event_type] ?? event.event_type}
            </span>
            <span className="text-[11.5px] text-muted-foreground">
              {formatUtcDateTime(event.occurred_at)}
            </span>
            {event.url && (
              <a
                href={event.url}
                target="_blank"
                rel="noreferrer"
                className="truncate text-[11.5px] text-primary hover:underline"
              >
                {event.url}
              </a>
            )}
            {event.bounce_description && (
              <span className="text-[11.5px] text-muted-foreground">
                {event.bounce_description}
              </span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function MessageDetailDrawer({
  message,
  onClose,
}: {
  message: Message | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [body, setBody] = useState<string | null>(null);
  const [bodyState, setBodyState] = useState<"idle" | "loading" | "gone">("idle");

  useEffect(() => {
    if (!message) {
      setDetail(null);
      setBody(null);
      setBodyState("idle");
      return;
    }
    setIsLoading(true);
    CommunicationsService.getMessageAsync(message.channel, message.id)
      .then(setDetail)
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false));
  }, [message]);

  async function loadBody() {
    if (!message) return;
    setBodyState("loading");
    const html = await CommunicationsService.getEmailBodyAsync(message.id);
    if (html === null) {
      // Expected for anything past Postmark's 45-day content retention, so this reads
      // as an explanation rather than an error.
      setBodyState("gone");
      return;
    }
    setBody(html);
    setBodyState("idle");
  }

  const isEmail = message?.channel === "email";

  return (
    <Sheet open={message !== null} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="flex w-full flex-col gap-5 overflow-y-auto sm:max-w-[560px]">
        <SheetHeader>
          <SheetTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              {isEmail ? (
                <Mail className="size-4 flex-none text-muted-foreground" />
              ) : (
                <MessageSquare className="size-4 flex-none text-muted-foreground" />
              )}
              {isEmail ? "Email" : "Text message"}
            </span>
          </SheetTitle>
          <SheetDescription>
            {message ? formatUtcDateTime(message.created_at) : ""}
          </SheetDescription>
        </SheetHeader>

        {message && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={cn(
                  "gap-1.5 rounded-[7px] font-normal",
                  statusBadgeClasses(message.status),
                )}
              >
                <span
                  className={cn(
                    "size-1.5 flex-none rounded-full",
                    statusDotClasses(message.status),
                  )}
                />
                {statusLabel(message.channel, message.status)}
              </Badge>
              <span className="text-[12px] text-muted-foreground">
                {SOURCE_LABELS[message.source] ?? message.source}
                {message.source_label ? ` · ${message.source_label}` : ""}
              </span>
            </div>

            {(detail?.error_message || message.error_code) && (
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Why it didn't arrive
                </div>
                <div className="mt-1 break-words text-[12.5px] text-foreground">
                  {detail?.error_message || `Error ${message.error_code}`}
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Recipient" value={message.recipient} />
              <Field label="Guest" value={message.guest_name} />
              {isEmail && <Field label="Subject" value={message.subject} />}
              <Field
                label={isEmail ? "Postmark ID" : "Twilio SID"}
                value={message.provider_message_id}
              />
              {isEmail && (
                <Field
                  label="Engagement"
                  value={`${message.open_count} opens · ${message.click_count} clicks`}
                />
              )}
              {!isEmail && detail?.num_segments != null && (
                <Field
                  label="Segments"
                  value={`${detail.num_segments}${
                    detail.price ? ` · ${detail.price} ${detail.price_unit}` : ""
                  }`}
                />
              )}
            </div>

            <div className="grid gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                History
              </span>
              {isLoading ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="h-3 w-32" />
                </div>
              ) : (
                <Timeline events={detail?.events ?? []} />
              )}
            </div>

            <div className="grid gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {isEmail ? "Preview" : "Message"}
              </span>
              {body ? (
                <div
                  className="max-h-[420px] overflow-y-auto rounded-md border border-border bg-white p-3 text-[12.5px]"
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              ) : (
                <div className="whitespace-pre-wrap break-words rounded-md border border-border bg-muted/30 p-3 text-[12.5px] text-foreground">
                  {message.preview || "—"}
                </div>
              )}

              {isEmail && !body && bodyState !== "gone" && (
                <div>
                  <CuoButton
                    variant="outline"
                    label={bodyState === "loading" ? "Loading…" : "View full message"}
                    disabled={bodyState === "loading"}
                    onClick={loadBody}
                  />
                </div>
              )}
              {bodyState === "gone" && (
                <span className="text-[11.5px] text-muted-foreground">
                  Postmark only keeps the full message for 45 days, so only the preview
                  above is still available.
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {message.guest_id && (
                <CuoButton
                  variant="outline"
                  label="View guest"
                  onClick={() => navigate(`/admin/guests/${message.guest_id}`)}
                />
              )}
              {message.automation_run_id && (
                <CuoButton
                  variant="outline"
                  label="View automation"
                  onClick={() => navigate("/admin/tools/automations")}
                />
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
